import { createI18n } from 'vue-i18n'

export type AppLocale = 'vi' | 'zh' | 'en'

export const LOCALE_STORAGE_KEY = 'app_locale'

export const APP_LOCALES: AppLocale[] = ['vi', 'zh', 'en']

const FALLBACK_LOCALE: AppLocale = 'vi'

export function getStoredLocale(): AppLocale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  const raw = ({ 'zh-CN': 'zh', 'vi-VN': 'vi', 'en-US': 'en' } as Record<string, string>)[stored || ''] || stored
  if (raw === 'vi' || raw === 'zh' || raw === 'en') return raw
  return 'vi'
}

type LocaleMessages = Record<string, unknown>

const localeLoaders: Record<AppLocale, () => Promise<{ default: LocaleMessages }>> = {
  vi: () => import('./locales/vi'),
  zh: () => import('./locales/zh'),
  en: () => import('./locales/en'),
}

export const i18n = createI18n({
  legacy: false,
  locale: getStoredLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  messages: {},
})

const loadedLocales = new Set<AppLocale>()

export function resetLoadedLocales(): void {
  loadedLocales.clear()
}

export async function loadLocaleMessages(locale: AppLocale, force = false): Promise<void> {
  if (!force && loadedLocales.has(locale)) return
  const messages = await localeLoaders[locale]()
  i18n.global.setLocaleMessage(locale, messages.default)
  loadedLocales.add(locale)
}

/** Ensure active locale (and fallback) messages are loaded, then switch. */
export async function setI18nLocale(locale: AppLocale): Promise<void> {
  // Switch immediately when messages are already cached so DB/i18n labels update without reload.
  if (loadedLocales.has(locale)) {
    i18n.global.locale.value = locale
    return
  }

  await Promise.all([
    loadLocaleMessages(locale),
    locale === FALLBACK_LOCALE ? Promise.resolve() : loadLocaleMessages(FALLBACK_LOCALE),
  ])
  i18n.global.locale.value = locale
}

/** Dev HMR: apply new locale modules from accept payload (dynamic import() stays stale). */
if (import.meta.hot) {
  const applyLocaleHot = (locale: AppLocale, mod: { default?: LocaleMessages } | undefined) => {
    if (mod?.default) {
      i18n.global.setLocaleMessage(locale, mod.default)
      loadedLocales.add(locale)
      return
    }
    // Accept bubbled without module — force full reload so locale keys cannot stay stale.
    import.meta.hot?.invalidate()
  }

  const reloadAllLocaleMessages = async () => {
    await Promise.all(APP_LOCALES.map(async (locale) => {
      loadedLocales.delete(locale)
      await loadLocaleMessages(locale, true)
    }))
  }

  import.meta.hot.accept('./locales/vi', (mod) => applyLocaleHot('vi', mod))
  import.meta.hot.accept('./locales/en', (mod) => applyLocaleHot('en', mod))
  import.meta.hot.accept('./locales/zh', (mod) => applyLocaleHot('zh', mod))

  for (const dep of [
    './locales/supplier-settlement-messages',
    './locales/enterprise-settlement-messages',
    './locales/enterprise-workbench-messages',
    './locales/module-ui-messages',
  ]) {
    import.meta.hot.accept(dep, () => {
      void reloadAllLocaleMessages()
    })
  }
}
